import { DG } from '@cotera/sdk/core';
import {
  BaseModel,
  ReadonlyWatchable,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { TargetableModel } from '@cotera/client/v0/actions/framework/types';
import { Relation } from '@cotera/era';
import { Result } from 'neverthrow';
import { Assert } from '@cotera/utilities';

export class ExpressionColumnModel
  extends BaseModel
  implements TargetableModel
{
  readonly currentNameInGraph: Watchable<string>;
  readonly formState: Watchable<{
    name: string;
    eraQLEditorValue: string;
  }>;
  readonly config: ReadonlyWatchable<DG.Graph.EraQlColConfig | null>;
  readonly datasetRel: ReadonlyWatchable<Result<
    DG.Graph.RelItem,
    DG.Graph.GraphError
  > | null>;
  readonly preview = Watchable.fromValue<Relation | null>(null);
  readonly isNew: boolean;
  readonly datasetName: string;
  readonly pageTitle: Watchable<string>;
  readonly changes: ReadonlyWatchable<Record<string, boolean>>;

  constructor(
    readonly inputs: {
      dataset: string;
      column: string;
      graph: ReadonlyWatchable<DG.BuildExplorer>;
    },
    opts: {
      targeted: boolean;
      isNew: boolean;
    }
  ) {
    super(`${inputs.dataset}-${inputs.column}`);

    this.isTargeted = opts.targeted;
    this.isNew = opts.isNew;
    this.datasetName = inputs.dataset;

    this.currentNameInGraph = Watchable.fromValue(inputs.column);

    this.datasetRel = Watchable.from((get) => {
      const graph = get(inputs.graph);

      return (
        graph
          .forSymbol(inputs.dataset)
          ?.builtNode.map((r) => {
            Assert.assert(r.item.t === 'rel');
            return r.item;
          })
          .mapErr((x) => x.err) ?? null
      );
    });

    this.config = Watchable.from((get) => {
      const res =
        get(inputs.graph).getColumn({
          rel: inputs.dataset,
          name: get(this.currentNameInGraph),
        })?.config ?? null;

      if (res === null) {
        return null;
      }

      Assert.assert(res.t === 'eraql');

      return res;
    });

    const meta = this.config.snapshot();

    this.formState = Watchable.fromValue({
      name: this.currentNameInGraph.snapshot(), // TODO: use var.get() or get(var)?
      eraQLEditorValue: meta?.source ?? '',
    });

    this.changes = Watchable.from((get) => ({
      eraQL: get(this.formState).eraQLEditorValue !== get(this.config)?.source,
    }));

    this.pageTitle = Watchable.fromValue(
      `${this.inputs.dataset}/${this.currentNameInGraph.snapshot()}`
    );
  }

  isTargeted: boolean;

  target(): void {
    this.isTargeted = true;
  }

  markAsNotTargeted(): void {
    this.isTargeted = false;
  }
}
